import React, { useRef } from "react";
import { PersonalizationValue, TriggerStepCreate } from "@dots-projects/types";
import { Controller, useForm } from "react-hook-form";
import { Alert, Select } from "antd";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import { loadWebhookKey } from "../../lib/queries";
import { TriggerEditorVariablesSelector } from "./TriggerEditorVariablesSelector";
const { Option } = Select;

/**
 * Renders the form for a "Zapier step"
 */
export function StepFormZapier({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(step.type === "send_webhook");

  const { state } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;

  const svixAppId = useRef(localStorage.getItem("svix-app-id") as string);

  const defaultValues = {
    eventType: step.configs.eventType,
    //TODO: define payload type, what kind of payload could be sent to zapier?
    message: (step.configs.payload as any)?.message,
    name: step.name,
  };

  const form = useForm({
    defaultValues,
  });
  const { handleSubmit, control } = form;
  const { dispatch, onPersist } = useTriggerEditorStateDispatch();

  const automations = [
    {
      value: "automation.email_capture",
      label: "Email",
    },
    {
      value: "automation.name",
      label: "Name",
    },
    {
      value: "automation.referral_source",
      label: "Referral Source",
    },
    {
      value: "automation.location",
      label: "Location",
    },
    {
      value: "automation.other",
      label: "Other",
    },
  ];

  const defaultEventTypeValue = automations.find((automation) => {
    return automation.value === defaultValues.eventType;
  });

  const onSubmit = async (data: any) => {
    if (svixAppId.current === null) {
      await loadWebhookKey();
      svixAppId.current = localStorage.getItem("svix-app-id") as string;
    }
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.configs.appId = localStorage.getItem("svix-app-id") as string;
      stepDraft.configs.eventType = data.eventType;
      stepDraft.configs.payload = {
        //TODO we should implement required fields from backend side
        ...(data.message
          ? { message: `{{${data.message.replaceAll(/{|}/g, "")}}}` }
          : null),
        username: `{{user.username}}`,
        userId: `{{user.id}}`,
        discriminator: `{{user.discriminator}}`,
        communityId: localStorage.getItem("guild-id") as string,
      };

      stepDraft.name = data.name;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  console.log("default values", defaultValues);

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      customTitle={"Push to Zapier"}
      form={form}
      step={step}
      customDescription={
        <p>
          Make sure you have the Dots app on Zapier by{" "}
          <a
            href="https://zapier.com/developer/public-invite/159040/4b2f4c51fd6b15801b6ba251a448d30e/"
            target="_blank"
            rel="noreferrer"
          >
            clicking this link
          </a>
        </p>
      }
    >
      <FormLabel label="What kind of response is this?" />
      <Controller
        control={control}
        name="eventType"
        render={({ field }) => (
          <>
            <Select
              disabled={readOnly}
              showSearch
              placeholder={"Select kind of response"}
              onChange={field.onChange}
              defaultValue={defaultEventTypeValue?.label || null}
            >
              <Option key={-1} value={-1} name="" disabled>
                {"Select kind of response"}
              </Option>
              {automations.map((automation, index) => {
                return (
                  <Option
                    key={automation.value}
                    value={automation.value}
                    name={automation.label}
                    id={index}
                  >
                    {automation.label}
                  </Option>
                );
              })}
            </Select>
          </>
        )}
      />
      <Alert
        className={"dots-note"}
        message="Note"
        description="For now, you can only map one response to one kind of response. If you need additional help on this, please reach out to support!"
        type="info"
      />

      <FormLabel label="Pick which response to send" />
      <TriggerEditorVariablesSelector
        triggerVariables={insertVariables}
        defaultValue={defaultValues.message}
        control={control}
        readOnly={readOnly}
        fieldName={"message"}
        placeholder={"Pick which response to send"}
      />
    </StepFormWithCommonParts>
  );
}
